import { SurgeryDiagnosis } from '@/models/GeneralSurgery'
import { GeneralSurgery, SurgeryProcedure } from '../models/GeneralSurgery'
import { Option } from '../models/Specialty'

const VALUE_RESOLVERS = {
  'single-select': findOptionMatch,
  'multi-select': findOptionMatch,
  'hint-field-multiple': findOptionMatchesFreeText,
  'hint-field': keepValue,
  'age-field': keepValue,
  'text-area': keepValue,
  'text-field': keepValue,
  'date-picker': parseDate,
  icd10: findICD10,
  'icd10-sec': findICD10,
  icdo: findICD10,
  autonomy: findUser,
  checkbox: findCheckboxMatch,
  'icdo-topography': findICD10,
  'icdo-histology': findICD10,
  'multi-select-tree': findOptionMatches,
  'single-select-tree': findOptionMatch,
  'surgical-autonomy': findOptionMatch
}

const CUSTOM_RESOLVERS = {
  // Anesthesia
  'loco-regional-anesthesia': findOptionMatches,
  asa: findOptionMatch,
  cam: keepValue,
  specialty: findOptionMatch,
  internship: findOptionMatch,

  // General Surgery
  'classification-benign-suspicious-malignant': findOptionMatch,
  'anatomic-region': findAnatomicRegion,
  'surgery-diagnosis': findSurgeryDiagnosis,
  'surgery-procedure': findSurgeryProcedures,
  'surgery-autonomy': fillAutonomy,
  'complications-clavien-dindo': findClavienDindo
}

function getResolver(component) {
  if (component.type === 'custom' || component.id === 'internship') {
    return (
      CUSTOM_RESOLVERS[component.id] ||
      function (value) {
        return { text: value, value: value, changed: true }
      }
    )
  } else {
    return (
      VALUE_RESOLVERS[component.type] ||
      function (value) {
        return { text: value, value: value, changed: true }
      }
    )
  }
}

function keepValue(value, _component) {
  return { text: value, value, changed: false }
}

function findICD10(value, options = []) {
  const values = value
    .split(',')
    .filter(value => !!value)
    .map(value => {
      return options.find(o => o.text === value)
    })

  return {
    text: values.length ? values.map(v => v.value).join(',') : '',
    value: values.length ? values.map(v => v.value) : [],
    changed: false,
    valid: values.length ? true : false
  }
}

function findUser(value, options) {
  const user = options.find(o => o.license == value)

  return {
    text: user
      ? user.firstName + ' ' + user.lastName + ' (' + user.license + ' )'
      : value,
    value: user ? user.id : value,
    changed: false,
    valid: true
  }
}

export function parseValue(value, component, log) {
  if (!value) return { text: '', value: undefined, changed: true }
  const resolve = getResolver(component)
  const matches = resolve(value, component.options, log)
  const result = Array.isArray(matches) ? formatMatches(matches) : matches
  return result
}

function parseDate(value) {
  const date = Date.parse(value)
  const dateText = Number.isNaN(date) ? '' : new Date(date).toLocaleDateString()
  return { text: dateText, value: date, changed: Number.isNaN(date) }
}

function formatMatches(matches) {
  return {
    text: matches.map(match => match.text).join(', '),
    value: matches.map(match => match.value),
    changed: !!matches.find(match => match.changed)
  }
}

function findOptionMatches(values = [], options = []) {
  if (typeof values === 'string' || values instanceof String) {
    values = values.split(',')
  }

  return values
    .filter(value => !!value)
    .map(value => {
      return findOptionMatch(value, options)
    })
}

function findOptionMatchesFreeText(values, options) {
  return findOptionMatches(values, options).map(v => ({ ...v, changed: false }))
}

function findOptionMatch(value, options) {
  if (value && options && options.length) {
    /*
    const matches = stringSimilarity.findBestMatch(
      `${value}`.toUpperCase().replace(/\s/g, ''),
      options.map(o => o.text.toUpperCase())
    )

    const bestMatchIndex = matches.bestMatchIndex
    const bestMatchRating = matches.ratings[bestMatchIndex].rating
    const changed =
      bestMatchRating < 1 &&
      options[bestMatchIndex].text !== value &&
      options[bestMatchIndex].value !== value

      return { ...options[bestMatchIndex], changed: false }
    */

    const option = options.find(o => o.text === value || o.value === value)

    return option
      ? { ...option, changed: false }
      : { text: value, value, changed: true }
  } else {
    return { text: 'N/A', value: 'n/a', changed: true }
  }
}

function findCheckboxMatch(value) {
  const CHECKBOX_OPTIONS = [
    { value: true, text: 'Sim' },
    { value: false, text: 'Não' }
  ]
  return findOptionMatch(value, CHECKBOX_OPTIONS)
}

/**
 * Custom resolvers
 * kept in same file to avoid
 * circular dependency
 *  */

function fillAutonomy(value, _options, log) {
  const options = Option.query().where('component', 'surgery-autonomy').get()
  const match = findOptionMatch(value, options)

  log['autonomy'] = match.value
  log['autonomy:text'] = match.text
  log['autonomy:changed'] = false

  return match
}

function findClavienDindo(value, options) {
  const disabledOptions = options.map(option => {
    return { value: option.value + 'd', text: option.text + 'd' }
  })
  const fullOptions = [...options, ...disabledOptions]
  return findOptionMatch(value, fullOptions)
}

function findAnatomicRegion(value) {
  const options = GeneralSurgery.flattedOrganTree
  return findOptionMatch(value, options)
}

function findSurgeryDiagnosis(value) {
  const options = SurgeryDiagnosis.all().map(diagnosis => ({
    text: diagnosis.label,
    value: diagnosis.id
  }))

  const match = findOptionMatch(value, options)

  return match
}

function findSurgeryProcedures(value) {
  const options = SurgeryProcedure.all().map(procedure => ({
    text: procedure.label,
    value: procedure.id
  }))
  return findOptionMatches(value, options)
}

// convert to commonJS module to run
function test() {
  const OPTIONS = [
    { text: 'Epidural', value: 'epidural' },
    { text: 'Caudal', value: 'caudal' },
    { text: 'Plexus', value: 'plexus' }
  ]
  const COMPONENT = {
    id: 'anesthesia-proceduree',
    type: 'single-selectt',
    options: OPTIONS
  }

  const VALUES = 'epidural, caudalle, plexus'

  const result = parseValue(VALUES, COMPONENT)
}

// test()
